/* eslint-disable no-plusplus */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as yup from 'yup';
import { DefaultTable } from 'src/components';
import { ErrorMessage, Form, Formik } from 'formik';
import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { IBenefitType } from 'src/interfaces';
import BenefitsService, { DocumentsFormType } from 'src/services/benefits.service';
import {
  Loader,
  AlertAdapter,
  RegularButton,
  CommonDropdown,
  ICommonDropdownOption,
  DataTableCellDeleteButton,
} from 'pg-ui-library';
import { useBenefitDocuments } from '../../queries/benefit-document.query';
import {
  Container,
  EmptyContainer,
  FormWrapper,
  Input,
  InputLabel,
  BenefitsSettingsWrapper,
  FileUploadWrapper,
  FileUploadWrapperRemove,
  TrashIcon,
  WarningText,
  StyledErrorWrap,
  FooterButtonWrapper,
  HeadingDocument,
  Wrapper,
  HorizontalLine,
} from './styles';

type BenefitFormType = {
  name: string;
  fileName?: string;
  files?: FileList | null;
  imgKey?: string;
};

const DownloadLink = ({ id }: { id: string }) => (
  <a href={BenefitsService.downloadPlanDocument(id)} rel="noopener noreferrer">
    Download
  </a>
);

const documentColumns: TableColumn<DocumentsFormType>[] = [
  {
    name: 'Name',
    selector: (row: DocumentsFormType) => row.fileName || '',
    sortField: 'fileName',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan Document',
    selector: (row: DocumentsFormType) => row.planDocument || '',
    sortField: 'planDocument',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Size',
    selector: (row: DocumentsFormType) => {
      const fileSize = row.fileSize as string;
      if (fileSize) {
        return `${fileSize} Kb`;
      }
      return '';
    },
    sortField: 'fileSize',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Upload Date',
    selector: (row: DocumentsFormType) => (row.createdAt as string) || '',
    sortField: 'createdAt',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Link',
    button: true,
    cell: (row: any) => <DownloadLink id={row.id as string} />,
  },
];

export const BenefitDocumentWrapper: React.FC<{
  selectedBenefitType: IBenefitType | undefined;
  onClose: Function;
}> = ({ selectedBenefitType, onClose }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const [selectedPlanDocument, setSelectedPlanDocument] = useState<string>();
  const [dropdownOptionsPlanDocument, setDropdownOptionsPlanDocuments] = useState<ICommonDropdownOption[]>([]);
  const { data, isLoading, refetch, isFetching } = useBenefitDocuments(selectedBenefitType?.versionId || '');
  const { data: availplanDocuments } = useQuery(['available'], () =>
    BenefitsService.getPlanDocumentsTypes(selectedBenefitType?.type)
  );
  const mountDropdownPlanDocumentOptions = useCallback(() => {
    const cpnsOptions: ICommonDropdownOption[] = availplanDocuments?.data?.map((item: string) => ({
      key: item,
      label: item,
      value: item,
    }));
    cpnsOptions?.unshift({
      key: '',
      label: '',
      value: '',
    });
    setDropdownOptionsPlanDocuments(cpnsOptions);
  }, [availplanDocuments?.data]);

  useEffect(() => {
    refetch();
    mountDropdownPlanDocumentOptions();
  }, [availplanDocuments]);

  const mutation = useMutation((id: string) => BenefitsService.deletePlanDocument(id), {
    onSuccess: () => {
      AlertAdapter('Plan Document deleted', 'success');
      refetch();
    },
    onError: () => {
      AlertAdapter('Failed to delete', 'error');
    },
  });

  const deleteButtonWrapper = (onClick: Function) => (
    <DataTableCellDeleteButton style={{ width: '100%' }} onClick={() => (!mutation.isLoading ? onClick() : '')} />
  );

  const deleteConfig = (id: string) => {
    mutation.mutate(id);
  };

  const documentsColumnsWithButton = [
    ...documentColumns,
    {
      name: 'Delete',
      button: true,
      cell: (row: any) => deleteButtonWrapper(() => deleteConfig(row.id as string)),
    },
  ];

  const schema = yup.object({
    name: yup.string().required(t('global.form.required')).max(255),
    files: yup.mixed().test('type', 'We only support pdf', value => {
      if (value) {
        let allValid = true;
        for (let index = 0; index < value.length; index++) {
          const file = value[index];
          if (file.type !== 'application/pdf') {
            allValid = false;
          }
        }
        return allValid;
      }
      return true;
    }),

    fileName: yup.string(),
  });

  const save = async (values: BenefitFormType) => {
    const fd = new FormData();
    if (values.files) {
      for (let index = 0; index < values.files.length; index++) {
        const file = values.files.item(index);
        if (file) {
          fd.append('file', file, file?.name || '');
        }
      }
    }
    fd.append('type', selectedBenefitType?.type || '');
    fd.append('planDocument', selectedPlanDocument || '');
    fd.append('versionId', selectedBenefitType?.versionId || '');
    BenefitsService.planDocuments(fd).then(
      () => {
        queryClient.invalidateQueries({ queryKey: ['benefits/types'] });
        AlertAdapter('Document added', 'success');
        onClose();
        refetch();
        setDropdownOptionsPlanDocuments([]);
        mountDropdownPlanDocumentOptions();
      },
      (e: Error) => {
        AlertAdapter(e.message, 'error');
      }
    );
  };

  const mutationCreate = useMutation((variables: BenefitFormType) => save(variables));

  const onSubmit = async (values: BenefitFormType) => {
    await mutationCreate.mutateAsync(values);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '30px' }}>
      <Formik
        initialValues={{
          name: selectedBenefitType?.type ?? '',
          file: null,
          fileName: '',
          imgKey: selectedBenefitType?.imgURL ?? '',
        }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({ handleChange, isSubmitting, values, isValid, setFieldValue }) => (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <FormWrapper>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1',
                  marginTop: '20px',
                }}
              >
                <BenefitsSettingsWrapper>
                  <div
                    style={{
                      display: 'flex',
                      flex: '0 18%',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <InputLabel>Name</InputLabel>

                    <Input
                      style={{ width: '100%', border: '1px solid' }}
                      data-cy="benefit-modal-form-name"
                      id="name"
                      name="name"
                      placeholder="Name..."
                      value={values.name}
                      onChange={handleChange}
                      disabled
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flex: '0 15%',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <div>
                      <CommonDropdown
                        label="Document Type"
                        id="planDocument"
                        name="planDocument"
                        options={dropdownOptionsPlanDocument || []}
                        wrapperStyle={{ width: '100%', marginRight: '13px' }}
                        selectStyle={{ maxWidth: 'none', width: '100%', border: '1px solid' }}
                        setValue={setSelectedPlanDocument}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flex: '0 20%',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <InputLabel>Files</InputLabel>

                    <FileUploadWrapper
                      style={{
                        border: '1px solid',
                        background: 'aliceblue',
                        display: 'flex',
                        alignSelf: 'flex-start',
                        borderRadius: '10px',
                      }}
                    >
                      <Input
                        style={{ width: '450px' }}
                        data-cy="benefit-modal-form-file-name"
                        id="fileName"
                        name="fileName"
                        placeholder="Drag and drop or click to upload files"
                        value={values.fileName}
                        onChange={handleChange}
                        disabled
                      />
                      <FileUploadWrapperRemove
                        onClick={() => {
                          setFieldValue('files', null);
                          setFieldValue('fileName', '');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        <TrashIcon />
                      </FileUploadWrapperRemove>
                    </FileUploadWrapper>
                    <Input
                      ref={fileInputRef}
                      style={{
                        width: '90%',
                        zIndex: 1,
                        marginTop: '-45px',
                        cursor: 'pointer',
                        opacity: '0',
                      }}
                      data-cy="benefit-modal-form-file"
                      type="file"
                      id="files"
                      name="files"
                      accept=".pdf"
                      multiple
                      placeholder="Drag and drop or click to upload a file"
                      onChange={e => {
                        const { files } = e.target;
                        let filesName = '';
                        if (files) {
                          setFieldValue('files', files);

                          for (let index = 0; index < files.length; index++) {
                            const file = files.item(index);
                            if (file) {
                              filesName = [filesName, file.name].join(' ');
                            }
                          }
                          setFieldValue('fileName', filesName);
                        } else {
                          setFieldValue('files', null);
                          setFieldValue('fileName', '');
                        }
                      }}
                    />
                    <ErrorMessage name="files" component={StyledErrorWrap} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flex: '0 30%',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <WarningText>
                      <span>Warning: </span>
                      The files with the same name will be replaced
                    </WarningText>
                  </div>
                </BenefitsSettingsWrapper>
              </div>
            </FormWrapper>
            <FooterButtonWrapper>
              <div>
                <RegularButton
                  data-cy="benefit-modal-submit-button"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (!isValid) {
                      AlertAdapter('You need to fill name and a valid pdf document', 'warning');
                      return;
                    }
                    onSubmit(values);
                  }}
                >
                  {t('global.form.save')}
                </RegularButton>
              </div>
            </FooterButtonWrapper>
          </Form>
        )}
      </Formik>
      <Wrapper>
        <HeadingDocument>Documents</HeadingDocument>
        <HorizontalLine />
      </Wrapper>
      {isLoading || isFetching ? (
        <EmptyContainer>
          <Loader />
        </EmptyContainer>
      ) : (
        <Container>
          <DefaultTable
            columns={documentsColumnsWithButton}
            data={data || []}
            progressPending={isLoading || isFetching}
            pagination={false}
          />
        </Container>
      )}
    </div>
  );
};
